import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { css, Toast, Spinner, PageHeader, Modal, Label, MontoInput, formatMiles } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';

// Presupuestos mensuales por dirección y centro de costos, con semáforo del
// gasto acumulado del mes (los 4 tipos de documento, neto con impuestos,
// SOLO estados "por pagar" y "pagada"). Admin/finanzas asignan; el director
// ve su dirección.

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Barra de consumo: verde <80%, ámbar 80-100%, rojo >100%
function BarraConsumo({ gastado, presupuesto }) {
  if (presupuesto == null) return <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>Sin presupuesto</span>;
  const pct = presupuesto > 0 ? (gastado / presupuesto) * 100 : (gastado > 0 ? 200 : 0);
  const color = pct > 100 ? 'var(--danger)' : pct >= 80 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
      <div style={{ flex: 1, height: 7, borderRadius: 99, background: 'var(--hair-soft)', overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 99,
          background: color, transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
      <span style={{ fontSize: 11.5, fontWeight: 700, color, fontFamily: 'var(--digit-font)', minWidth: 44, textAlign: 'right' }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

export default function Presupuestos() {
  const { user } = useAuth();
  const puedeEditar = ['admin', 'finanzas'].includes(user?.rol);
  const [mes, setMes] = useState(mesActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  // Modal de asignación: { tipo, nombre, monto (crudo), actual }
  const [editModal, setEditModal] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get(`/presupuestos/estado?mes=${mes}`)); }
    catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [mes]);

  useEffect(() => { load(); }, [load]);

  const abrirEdicion = (tipo, nombre, actual) =>
    setEditModal({ tipo, nombre, monto: actual != null ? String(actual) : '', actual });

  const guardar = async () => {
    const { tipo, nombre, monto } = editModal;
    setGuardando(true);
    try {
      if ((monto || '').trim() === '') {
        await api.del(`/presupuestos?tipo=${tipo}&nombre=${encodeURIComponent(nombre)}`);
        toast_(`Presupuesto de "${nombre}" eliminado`);
      } else {
        await api.put('/presupuestos', { tipo, nombre, montoMensual: Number(monto) });
        toast_(`Presupuesto de "${nombre}" guardado`);
      }
      setEditModal(null);
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setGuardando(false); }
  };

  const alertas = data?.alertas || [];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <PageHeader title="Presupuestos por Centro de Costos"
        subtitle={puedeEditar ? 'Asigna el presupuesto mensual y vigila el gasto acumulado' : `Consumo mensual de tu dirección (${user?.direccion || '—'})`}
        actions={(
          <input type="month" value={mes} onChange={e => setMes(e.target.value || mesActual())}
            style={{ ...css.input(false), width: 170, cursor: 'pointer' }} />
        )} />

      {/* Alerta de rebases del mes */}
      {alertas.length > 0 && (
        <div style={{
          ...css.card, padding: '14px 18px', marginBottom: 18,
          border: '1px solid var(--danger-line)',
          background: 'var(--danger-bg)',
        }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--danger)', marginBottom: 6 }}>
            <ConIcono nombre="alerta">{alertas.length} presupuesto{alertas.length > 1 ? 's' : ''} rebasado{alertas.length > 1 ? 's' : ''} en {data.mes}</ConIcono>
          </div>
          {alertas.map((a, i) => (
            <div key={i} style={{ fontSize: 12.5, color: 'var(--danger)', padding: '2px 0' }}>
              • {a.tipo === 'direccion' ? `Dirección ${a.nombre}` : `${a.nombre}${a.direccion ? ` (${a.direccion})` : ''}`}:{' '}
              <b style={{ fontFamily: 'var(--digit-font)' }}>${fmt(a.gastado)}</b> de ${fmt(a.presupuesto)} autorizados{' '}
              (<b>+{fmt(a.gastado - a.presupuesto)}</b>)
            </div>
          ))}
        </div>
      )}

      {loading ? <div style={css.card}><Spinner /></div> : !data?.direcciones?.length ? (
        <div style={{ ...css.card, textAlign: 'center', color: 'var(--glass)', fontSize: 13.5 }}>
          No hay centros de costos con actividad ni presupuestos asignados.
        </div>
      ) : data.direcciones.map(dir => (
        <div key={dir.direccion} style={{ ...css.card, padding: 0, marginBottom: 16, overflow: 'hidden' }}>
          {/* Encabezado de la dirección con su presupuesto total */}
          <div style={{
            padding: '13px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            borderBottom: '1px solid var(--hair)',
            background: dir.excedido ? 'var(--danger-bg)' : 'var(--panel-hi)',
          }}>
            <span style={{ fontWeight: 800, fontSize: 13.5, color: dir.excedido ? 'var(--danger)' : 'var(--plasma)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ConIcono nombre="edificio">{dir.direccion}</ConIcono> {dir.excedido && <span style={{ marginLeft: 6 }}><ConIcono nombre="alerta" size={13}>REBASADO</ConIcono></span>}
            </span>
            {dir.nCentrosConPresupuesto > 0 && (
              <span title={`Suma de los presupuestos asignados a los ${dir.nCentrosConPresupuesto} centro(s) de esta dirección`}
                style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-soft)', fontFamily: 'var(--digit-font)', background: 'var(--plasma-dim)', border: '1px solid var(--plasma-line)', padding: '2px 10px', borderRadius: 99 }}>
                Σ centros: ${fmt(dir.presupuestoCentros)}
              </span>
            )}
            <span style={{ fontSize: 12, color: 'var(--glass)', marginLeft: 'auto' }}>
              Gastado: <b style={{ color: 'var(--ink)', fontFamily: 'var(--digit-font)' }}>${fmt(dir.gastado)}</b>
              {dir.presupuesto != null && <> / ${fmt(dir.presupuesto)}</>}
            </span>
            <div style={{ width: 200 }}><BarraConsumo gastado={dir.gastado} presupuesto={dir.presupuesto} /></div>
            {puedeEditar && (
              <button onClick={() => abrirEdicion('direccion', dir.direccion, dir.presupuesto)} style={css.btnGhost}>
                {dir.presupuesto != null ? <ConIcono nombre="editar" size={13}>Presupuesto</ConIcono> : <ConIcono nombre="mas" size={13}>Presupuesto</ConIcono>}
              </button>
            )}
          </div>
          {/* Centros de la dirección */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr>
                {['Centro de costos', 'Gastado (mes)', 'Presupuesto mensual', 'Consumo', puedeEditar ? '' : null].filter(c => c !== null).map(c => (
                  <th key={c} style={{ padding: '9px 18px', textAlign: c === 'Centro de costos' ? 'left' : 'right', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dir.centros.map(c => (
                <tr key={c.centro} style={{ borderTop: '1px solid var(--hair-soft)', background: c.excedido ? 'var(--danger-bg)' : 'transparent' }}>
                  <td style={{ padding: '9px 18px', color: 'var(--ink)', fontWeight: 600 }}>
                    {c.centro} {c.excedido && <span title="Presupuesto rebasado" style={{ color: 'var(--danger)', verticalAlign: 'middle' }}><Icono nombre="alerta" size={13} /></span>}
                  </td>
                  <td style={{ padding: '9px 18px', textAlign: 'right', fontFamily: 'var(--digit-font)', color: c.excedido ? 'var(--danger)' : 'var(--ink)', fontWeight: c.excedido ? 800 : 500 }}>
                    ${fmt(c.gastado)}
                  </td>
                  <td style={{ padding: '9px 18px', textAlign: 'right', fontFamily: 'var(--digit-font)', color: c.presupuesto != null ? 'var(--ink-soft)' : 'var(--glass)' }}>
                    {c.presupuesto != null ? `$${fmt(c.presupuesto)}` : '—'}
                  </td>
                  <td style={{ padding: '9px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}><BarraConsumo gastado={c.gastado} presupuesto={c.presupuesto} /></div>
                  </td>
                  {puedeEditar && (
                    <td style={{ padding: '9px 18px', textAlign: 'right', width: 60 }}>
                      <button onClick={() => abrirEdicion('centro', c.centro, c.presupuesto)} title="Asignar presupuesto" aria-label="Asignar presupuesto"
                        style={{ ...css.btnGhost, padding: '4px 10px', fontSize: 12 }}>
                        {c.presupuesto != null ? <Icono nombre="editar" size={15} /> : <Icono nombre="mas" size={15} />}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Asignar / editar presupuesto */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)}
        title={editModal?.tipo === 'direccion' ? `Presupuesto de la dirección` : `Presupuesto del centro`}>
        {editModal && (
          <div>
            <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 700, marginBottom: 14 }}>
              {editModal.nombre}
            </div>
            <Label>Monto mensual autorizado (deja vacío para quitar el presupuesto)</Label>
            <MontoInput value={editModal.monto} onChange={v => setEditModal(m => ({ ...m, monto: v }))} />
            {editModal.actual != null && (
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 6 }}>
                Actual: ${formatMiles(String(editModal.actual))} · aplica igual todos los meses
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button onClick={() => setEditModal(null)} style={css.btnGhost}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{ ...css.btn(), opacity: guardando ? 0.6 : 1 }}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Toast {...(toast || {})} onClose={() => setToast(null)} />
    </div>
  );
}
